import actualizarImg from '@assets/actualizar.png'
import logoAtemporal from '@assets/atemporal/LogoAtemporalMenu.svg'
import { showArmRestrictionNotice } from '@components/ArmsCushionsPanel/ArmRestrictionNoticeModal'
import { HoverZoomPreviewOverlay, useHoverZoomPreview } from '@components/HoverZoomPreview'
import { MENU_INITIAL_KEY } from '@constants'
import { useFetchMenu, type Submenu } from '@hooks/useFetchMenu'
import { useViewportLayout } from '@hooks/useViewportLayout'
import type { ViewportLayout } from '../../constants/viewport'
import { MenuProxy } from '@proxies/menu'
import * as Tooltip from '@radix-ui/react-tooltip'
import { useAppStore, drawSnapLog } from '@store'
import { filterMenuSubmenusForToolbar } from '@utils/atemporal/relaxArmVariants'
import {
  childBaseCatalogFromUnit,
  getSeatHeightCmForNewUnitsFromScene,
  resolveDrawCatalogWithSeatHeight,
  resolveReplaceCatalogWithSeatHeight,
  resolveUnitSeatHeightCm,
} from '@utils/atemporal/seatHeightCatalog'
import { placementForSeatHeightChange } from '@utils/atemporal/seatHeightPlacement'
import { withCameraFitBlocked } from '@utils/camera/withCameraFitBlocked'
import { runWithHistoryBatchAsync } from '@utils/historyRecording'
import is2DMode from '@utils/is2DMode'
import {
  buildApproachMatrixForArm,
  buildBr12FineTunedMatrix,
  decomposeMatrixPosition,
  getArmSideSign,
} from '@utils/linkedArms/armPlacement'
import { evaluateSingleArmSeriesPlacement } from '@utils/linkedArms/resolveSingleArmSeriesPlacement'
import {
  ARM_NOTICE_ONLY_ONE_SIDE,
  allowedArmSideForUnit,
  isSingleArmSeries,
  pickArmSnapshotToKeepForSingleArmSeries,
} from '@utils/linkedArms/singleArmSeriesRules'
import {
  CORNER_INSERT_NO_FREE_END_NOTICE,
  collectArmsAtCornerInsertEnd,
  isCornerInsertCatalog,
  removeArmsForCornerInsert,
  resolveChainedDrawPlan,
  resolveCornerInsertPlan,
} from '@utils/modularSnap'
import { cloneLinkedItemTexturesForInheritance } from '@utils/sceneObjects/getExistingTexturesFromScene'
import { localizeCatalogLabel } from '@utils/i18n/catalogLabels'
import { getCatalogDimensionLabels, ui } from '@utils/i18n/uiCopy'
import getId from '@utils/sceneObjects/getId'
import { getRelativeMatrix } from '@utils/sceneObjects/items/getRelativeMatrix'
import {
  RINCONERO_DRAW_DEBUG,
  defaultThicknessForDrawCatalog,
  defaultWidthForDrawCatalog,
  isCornerXLBrazoCatalog,
  isModularCornerSnapCatalog,
  removeArmsForRinconeroDraw,
  resolveAnchorUnitForRinconeroDraw,
  resolveAttachEndForCornerDraw,
  resolveDrawSnapSideOnRinconero,
  rinconeroStraightModuleUsesLegSnap,
} from '@utils/sceneObjects/items/rinconeroUtils'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { Matrix4, Quaternion, Vector3 } from 'three'

type MenuNavEntry = {
  clavemenuabrir: string
  texto: string
}

const filterPrincipalSubmenus = (submenus: Submenu[] = []) =>
  filterMenuSubmenusForToolbar('PRINCIPAL', submenus).filter(
    (item) =>
      item.id !== 14 &&
      item.id !== 15 &&
      item.claveMenuItem !== '2COJINESRESPALDO' &&
      item.claveMenuItem !== '3COJINES60X602COJINES50X50',
  )

const filterSubmenuItems = (submenus: Submenu[] = [], menuKey = '') =>
  filterMenuSubmenusForToolbar(menuKey, submenus)

const formatDimensionLabel = (label: string, value: unknown) =>
  `${label}: ${typeof value === 'number' && !Number.isNaN(value) ? `${value / 10}cm` : '-'}`

/** Densidad del catálogo de módulos solo en sheets touch (Desktop docked intacto). */
function getEmbeddedModulesGridClass(layout: ViewportLayout): string {
  if (layout === 'mobile') return 'grid grid-cols-2 gap-2'
  if (layout === 'tabletV') return 'grid grid-cols-3 gap-2.5'
  return 'grid grid-cols-4 gap-3' // tabletH
}

function getEmbeddedModuleScale(layout: ViewportLayout): number {
  if (layout === 'mobile') return 0.68
  if (layout === 'tabletV') return 0.72
  return 0.78 // tabletH
}

function getEmbeddedModuleMaxHeight(layout: ViewportLayout): number {
  if (layout === 'mobile') return 72
  if (layout === 'tabletV') return 84
  return 96 // tabletH
}

export function Toolbar({ presentation = 'docked' }: { presentation?: 'docked' | 'embedded' }) {
  const layout = useViewportLayout()
  const { menuData: principalMenuData } = useFetchMenu(MENU_INITIAL_KEY)

  const [navStack, setNavStack] = useState<MenuNavEntry[]>([])
  const currentMenuKey = navStack.at(-1)?.clavemenuabrir ?? MENU_INITIAL_KEY
  const isPrincipalView = navStack.length === 0
  const currentCategoryTitle = navStack.at(-1)?.texto ?? ''

  const { menuData: activeRawMenuData } = useFetchMenu(currentMenuKey, currentMenuKey, null)

  const menuData = (() => {
    if (isPrincipalView) {
      return principalMenuData
        ? {
            ...principalMenuData,
            submenus: filterPrincipalSubmenus(principalMenuData.submenus),
          }
        : principalMenuData
    }
    return activeRawMenuData
      ? {
          ...activeRawMenuData,
          submenus: filterSubmenuItems(activeRawMenuData.submenus, currentMenuKey),
        }
      : activeRawMenuData
  })()

  const toolbarBreadcrumbInit = useAppStore((s) => s.toolbarBreadcrumbInit)
  const isModalOpen = useAppStore((s) => s.isModalOpen)
  const toolbarMenuToOpen = useAppStore((s) => s.toolbarMenuToOpen)
  const setToolbarMenuToOpen = useAppStore((s) => s.setToolbarMenuToOpen)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const [hasInitialized, setHasInitialized] = useState(false)
  const [searchText, setSearchText] = useState('')
  /** Catálogo aplanado de módulos dibujables bajo PRINCIPAL (para buscar desde el nivel superior). */
  const [principalCatalog, setPrincipalCatalog] = useState<Submenu[]>([])
  const [principalCatalogStatus, setPrincipalCatalogStatus] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle')
  const { hoverPreview, getHoverPreviewHandlers } = useHoverZoomPreview()

  const normalizedSearch = searchText.trim().toLowerCase()
  const normalizeWidthToCm = (rawValue: unknown): number | null => {
    const numericValue =
      typeof rawValue === 'number'
        ? rawValue
        : typeof rawValue === 'string'
          ? Number(rawValue.replace(',', '.'))
          : NaN
    if (!Number.isFinite(numericValue) || numericValue <= 0) return null
    // En datos del menú, ancho/fondo suelen venir en mm (ej: 580 => 58cm).
    // Si ya viene en cm (ej: 58), lo dejamos tal cual.
    return numericValue >= 300 ? Math.round(numericValue / 10) : Math.round(numericValue)
  }
  const extractWidthCm = (submenu: any): number | null => {
    const fromDirectField = normalizeWidthToCm(submenu?.ancho)
    if (fromDirectField != null) return fromDirectField

    const sources = [
      submenu?.texto,
      submenu?.descripcion,
      submenu?.referencia,
      ...(Array.isArray(submenu?.args) ? submenu.args : []),
    ]
      .filter(Boolean)
      .map((v) => String(v))
      .join(' ')

    // Ejemplos: "Ancho: 58cm", "ancho 138 cm"
    const match = sources.match(/ancho\s*:?\s*(\d+(?:[.,]\d+)?)\s*cm?/i)
    if (!match) return null
    return normalizeWidthToCm(match[1])
  }

  const numericSearch = Number(normalizedSearch.replace(',', '.'))
  const hasNumericSearch = normalizedSearch.length > 0 && Number.isFinite(numericSearch)

  // Precargar módulos de todas las categorías de PRINCIPAL para que el buscador
  // funcione en el nivel superior (no solo dentro de un grupo abierto).
  useEffect(() => {
    if (!isPrincipalView) return
    const categories = filterPrincipalSubmenus(principalMenuData?.submenus || []).filter(
      (item) => Boolean(item.clavemenuabrir),
    )
    if (categories.length === 0) return

    let cancelled = false
    const proxy = new MenuProxy()
    const idiomaCultura = useAppStore.getState().idiomaCultura || 'ES'

    const fetchLeaves = async (menuKey: string, depth = 0): Promise<Submenu[]> => {
      const data = await proxy.getMenu({ clavemenu: menuKey }, idiomaCultura)
      const items = filterSubmenuItems(
        Array.isArray(data?.submenus) ? data.submenus : [],
        menuKey,
      )
      const leaves: Submenu[] = []
      for (const item of items) {
        if (item.clavemenuabrir && depth < 2) {
          try {
            leaves.push(...(await fetchLeaves(item.clavemenuabrir, depth + 1)))
          } catch {
            /* menú hijo no disponible */
          }
        } else if (!item.clavemenuabrir) {
          leaves.push(item)
        }
      }
      return leaves
    }

    setPrincipalCatalogStatus('loading')
    Promise.all(
      categories.map(async (cat) => {
        try {
          return await fetchLeaves(cat.clavemenuabrir)
        } catch {
          return [] as Submenu[]
        }
      }),
    )
      .then((groups) => {
        if (cancelled) return
        const seen = new Set<string>()
        const flat: Submenu[] = []
        for (const item of groups.flat()) {
          const key = [
            item.id,
            item.claveMenuItem,
            item.codigoUnidad,
            Array.isArray(item.args) ? item.args.join('|') : item.args,
          ].join('::')
          if (seen.has(key)) continue
          seen.add(key)
          flat.push(item)
        }
        setPrincipalCatalog(flat)
        setPrincipalCatalogStatus('ready')
      })
      .catch(() => {
        if (cancelled) return
        setPrincipalCatalogStatus('error')
      })

    return () => {
      cancelled = true
    }
  }, [isPrincipalView, principalMenuData])

  const visibleSubmenus = (() => {
    // En PRINCIPAL con texto de búsqueda: filtrar el catálogo aplanado de todos los grupos.
    const searchingFromPrincipal = isPrincipalView && normalizedSearch.length > 0
    const submenus = searchingFromPrincipal
      ? principalCatalog
      : menuData?.submenus || []
    if (!normalizedSearch) return menuData?.submenus || []

    if (hasNumericSearch) {
      const withWidth = submenus
        .map((submenu: any) => ({ submenu, widthCm: extractWidthCm(submenu) }))
        .filter((entry) => entry.widthCm !== null) as { submenu: any; widthCm: number }[]

      const maxWidthLowerOrEqual = withWidth
        .map((entry) => entry.widthCm)
        .filter((width) => width <= numericSearch)
        .reduce((max, width) => (width > max ? width : max), -Infinity)

      if (Number.isFinite(maxWidthLowerOrEqual)) {
        return withWidth
          .filter((entry) => entry.widthCm === maxWidthLowerOrEqual)
          .map((entry) => entry.submenu)
      }

      // Si no hay anchos <= buscado, mantener fallback textual.
    }

    return submenus.filter((submenu: any) => {
      const haystack = [
        submenu?.texto,
        submenu?.claveMenuItem,
        submenu?.codigoUnidad,
        submenu?.referencia,
        submenu?.descripcion,
        ...(Array.isArray(submenu?.args) ? submenu.args : []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(normalizedSearch)
    })
  })()

  // Verificar si hay scroll disponible y posición actual
  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = scrollContainerRef.current
        const hasScroll = scrollHeight > clientHeight
        setShowScrollButton(hasScroll)

        // Verificar si está exactamente en el inicio o al final (sin margen para ser más estricto)
        const isAtTop = scrollTop === 0
        const isAtBottomPosition = scrollTop + clientHeight >= scrollHeight - 1

        // Solo cambiar la dirección cuando realmente está en los extremos
        if (isAtTop) {
          setIsAtBottom(false) // En el inicio, mostrar flecha hacia abajo
          setHasInitialized(true)
        } else if (isAtBottomPosition) {
          setIsAtBottom(true) // Al final, mostrar flecha hacia arriba
          setHasInitialized(true)
        } else if (!hasInitialized) {
          // Solo en la inicialización, establecer el estado basándose en la posición inicial
          // Si está más cerca del final, mostrar arriba; si no, mostrar abajo
          const distanceFromTop = scrollTop
          const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
          setIsAtBottom(distanceFromBottom < distanceFromTop)
          setHasInitialized(true)
        }
        // Si está en el medio y ya se inicializó, NO cambiar el estado (mantener la dirección actual)
      }
    }

    checkScroll()
    // Verificar cuando cambian los datos del menú
    if (menuData?.submenus) {
      setTimeout(checkScroll, 100)
    }

    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScroll)
      window.addEventListener('resize', checkScroll)
      return () => {
        container.removeEventListener('scroll', checkScroll)
        window.removeEventListener('resize', checkScroll)
      }
    }
  }, [visibleSubmenus, hasInitialized])

  // Función para hacer scroll incremental
  const handleScrollClick = () => {
    if (!scrollContainerRef.current) return

    const container = scrollContainerRef.current
    const scrollAmount = 300 // Cantidad de píxeles a scrollear

    if (isAtBottom) {
      // Si está abajo, hacer scroll hacia arriba incrementalmente
      container.scrollBy({
        top: -scrollAmount,
        behavior: 'smooth',
      })
    } else {
      // Si está arriba, hacer scroll hacia abajo incrementalmente
      container.scrollBy({
        top: scrollAmount,
        behavior: 'smooth',
      })
    }
  }

  // Esto para la Sidebar
  useEffect(() => {
    if (!toolbarMenuToOpen || !principalMenuData?.submenus) return

    if (toolbarBreadcrumbInit && toolbarBreadcrumbInit.length > 0) {
      setNavStack(
        toolbarBreadcrumbInit.map((entry) => ({
          clavemenuabrir: entry.clavemenuabrir,
          texto: entry.texto,
        })),
      )
      setToolbarMenuToOpen(null)
      return
    }

    const menuToOpen = principalMenuData.submenus.find(
      (m) => m.clavemenuabrir === toolbarMenuToOpen,
    )
    if (menuToOpen) {
      setNavStack([{ clavemenuabrir: toolbarMenuToOpen, texto: menuToOpen.texto }])
    } else {
      setNavStack([])
    }
    setToolbarMenuToOpen(null)
  }, [toolbarMenuToOpen, principalMenuData?.submenus, setToolbarMenuToOpen, toolbarBreadcrumbInit])

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [currentMenuKey])

  const handleBackNavigation = () => {
    setNavStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : []))
    setSearchText('')
  }

  const handleBackToPrincipal = () => {
    setNavStack([])
    setSearchText('')
  }

  const drawItem = useAppStore((s) => s.drawItem)
  const editItem = useAppStore((s) => s.editItem)
  const setMode = useAppStore((s) => s.setMode)
  const mode = useAppStore((s) => s.mode)
  const setCurrentObjectID = useAppStore((s) => s.setCurrentObjectID)
  const unselect = useAppStore((s) => s.unselectAll)
  const select = useAppStore((s) => s.select)
  const setCollision = useAppStore((s) => s.setCollision)
  const setOBB = useAppStore((s) => s.setOBB)
  const replaceItem = useAppStore((s) => s.replaceItem)
  const setFrameloop = useAppStore.use.setFrameloop()
  const currentObjectID = useAppStore((s) => s.currentObjectID)
  const items = useAppStore((s) => s.items)
  const selectedIds = useAppStore((s) => s.layers[s.currentLayer].selected)

  
  const AUTO_GROUP_BASE_MENU_KEYS = new Set(['34851', '6-95', '6-100', '6-105', '6-150'])
  const MULTI_CUSHIONS_CATALOG = '3 cojines 60x60 2 cojines 50x50'
  const GLOBAL_CUSHION_CATALOG_KEY = 'atemporal:autoGroup:cushionCatalog'
  const GLOBAL_SEAT_STITCH_KEY = 'atemporal:autoGroup:seatStitchType'
  const AUTO_GROUP_DEBUG = false
  // Ajustes visuales para que asiento/cojines queden más integrados en la base
  const SEAT_EMBED_OFFSET_MM = 155
  const CUSHIONS_EMBED_OFFSET_MM = 280
  const PACK_EXTRA_Y_OFFSET_MM = 360
  const forceSceneRefresh = () => {
    const previousFrameloop = useAppStore.getState().frameloop
    setFrameloop('always')
    setTimeout(() => {
      setFrameloop(previousFrameloop)
    }, 700)
    if (typeof window === 'undefined') return
    const r3f = (window as any).r3f
    if (!r3f?.invalidate) return
    r3f.invalidate()
    requestAnimationFrame(() => r3f.invalidate?.())
    setTimeout(() => r3f.invalidate?.(), 40)
    setTimeout(() => r3f.invalidate?.(), 120)
  }
  const cloneMaterialBetweenLinkedSons = (
    sourceSon: any,
    targetSonId?: string,
    parentBaseId?: string,
  ) => {
    if (!targetSonId) return
    const targetSon = useAppStore.getState().items.get(targetSonId as any) as any
    if (!targetSon) return
    const sourceZones = sourceSon?.zones || {}
    const sourceTextures = Array.isArray(sourceSon?.textures) ? sourceSon.textures : []
    const targetTextures = Array.isArray(targetSon?.textures) ? targetSon.textures : []
    const parentBase = parentBaseId
      ? (useAppStore.getState().items.get(parentBaseId as any) as any)
      : null
    const parentZones = parentBase?.zones || {}
    const parentTextures = Array.isArray(parentBase?.textures) ? parentBase.textures : []
    const targetZones = targetSon?.zones || {}
    const targetZoneNames = Object.keys(targetZones)
    const nextZones = { ...targetZones }
    let hasAnyMaterialUpdate = false

    targetZoneNames.forEach((zoneName) => {
      const sourceMaterialByZone = sourceZones?.[zoneName]?.material
      const targetTexture = targetTextures.find((t: any) => t?.zoneName === zoneName)
      const targetZoneDisplay = String(targetTexture?.zoneDisplayName || '').toLowerCase()
      const sourceByDisplay = sourceTextures.find(
        (t: any) => String(t?.zoneDisplayName || '').toLowerCase() === targetZoneDisplay,
      )
      const sourceMaterialByDisplay = sourceByDisplay
        ? sourceZones?.[sourceByDisplay.zoneName]?.material
        : null
      const parentByDisplay = parentTextures.find(
        (t: any) => String(t?.zoneDisplayName || '').toLowerCase() === targetZoneDisplay,
      )
      const parentMaterialByDisplay = parentByDisplay
        ? parentZones?.[parentByDisplay.zoneName]?.material
        : null
      const chosen = sourceMaterialByZone || sourceMaterialByDisplay || parentMaterialByDisplay
      if (!chosen) return
      hasAnyMaterialUpdate = true
      nextZones[zoneName] = {
        ...nextZones[zoneName],
        material: { ...chosen },
      }
    })

    if (hasAnyMaterialUpdate) {
      editItem(targetSonId as string, { zones: nextZones } as any)
    }
  }
  const getSeatCatalogForBase = (baseCatalogID: string, stitchType: 'con' | 'sin' = 'con') => {
    const base = childBaseCatalogFromUnit(baseCatalogID)
    if (!base) return ''
    if (stitchType === 'con') {
      return `asiento ${base}`
    }
    // Asiento sin costura: misma referencia que la base (ej: 6-150 -> asientosin 6-150)
    return `asientosin ${base}`
  }
  const getDefaultCushionCatalogForBase = (baseCatalogID: string) => {
    const base = childBaseCatalogFromUnit(baseCatalogID)
    if (!base) return MULTI_CUSHIONS_CATALOG
    return `cojines ${base}`
  }
  const getCushionCatalogForTargetBase = (baseCatalogID: string, sourceCatalog?: string) => {
    const base = childBaseCatalogFromUnit(baseCatalogID)
    if (!base) return getDefaultCushionCatalogForBase(baseCatalogID)
    const source = String(sourceCatalog || '').toLowerCase()
    const isRectangular = source.includes('cojinesr')
    return `${isRectangular ? 'cojinesR' : 'cojines'} ${base}`
  }

  const getSeatHeightCmForNewUnits = () =>
    getSeatHeightCmForNewUnitsFromScene(useAppStore.getState().items)

  const getSeatStitchForNewUnits = () => {
    const state = useAppStore.getState()
    const baseItemsInScene = Array.from(state.items.values()).filter((it: any) => {
      if (!it) return false
      if (it.type !== 'linkedItem' && it.type !== 'items') return false
      const catalog = String(it.catalogID || '').toUpperCase()
      const isArm = /^BR\d+$/i.test(catalog)
      const isSeatOrCushionChild = catalog.includes('ASIENTO') || catalog.includes('COJIN')
      return !isArm && !isSeatOrCushionChild
    }) as any[]

    // Regla requerida: si no hay nada dibujado, la primera vez siempre con costura.
    if (baseItemsInScene.length === 0) return 'con'

    const fromScene = String((baseItemsInScene[0] as any)?.seatStitchType || '')
      .trim()
      .toLowerCase()
    if (fromScene === 'sin') return 'sin'
    if (fromScene === 'con') return 'con'

    if (typeof window === 'undefined') return 'con'
    const raw = String(window.localStorage.getItem(GLOBAL_SEAT_STITCH_KEY) || '')
      .trim()
      .toLowerCase()
    return raw === 'sin' ? 'sin' : 'con'
  }

  const getSceneUniqueCushionCatalog = () => {
    const state = useAppStore.getState()
    const sceneCushion = Array.from(state.items.values()).find((it: any) => {
      return (
        it?.type === 'linkedSon' &&
        String(it?.catalogID || '')
          .toUpperCase()
          .includes('COJIN')
      )
    }) as any
    return String(sceneCushion?.catalogID || '').trim() || null
  }
  const getSceneCushionCatalogForBase = (baseCatalogID: string) => {
    const state = useAppStore.getState()
    const expected = `cojines ${String(baseCatalogID || '').trim()}`.toUpperCase()
    if (!expected.trim()) return null
    const sceneCushion = Array.from(state.items.values()).find((it: any) => {
      if (it?.type !== 'linkedSon') return false
      const catalog = String(it?.catalogID || '')
        .trim()
        .toUpperCase()
      return catalog === expected
    }) as any
    return String(sceneCushion?.catalogID || '').trim() || null
  }

  const getAutoGroupCatalogs = (claveMenuItem?: string, args?: string[] | null) => {
    if (!claveMenuItem || !AUTO_GROUP_BASE_MENU_KEYS.has(claveMenuItem)) return null
    const rawBase = args && args.length > 0 ? args[0] : ''
    if (!rawBase) return null
    const baseCatalogID = resolveDrawCatalogWithSeatHeight(
      rawBase,
      getSeatHeightCmForNewUnits(),
    )
    const seatCatalogID = getSeatCatalogForBase(baseCatalogID, getSeatStitchForNewUnits())
    // Elegir cojín por base actual para evitar contaminación entre módulos
    // (ej: 1-95 no debe forzar cojines en un 6-95).
    // Si ya existe ese cojín específico en escena, reutilizarlo; si no, usar default por base.
    const cushionsCatalogID =
      getSceneCushionCatalogForBase(baseCatalogID) ||
      getSceneUniqueCushionCatalog() ||
      getDefaultCushionCatalogForBase(baseCatalogID)
    return [baseCatalogID, seatCatalogID, cushionsCatalogID]
  }

  const selectedReplaceItem =
    currentObjectID && selectedIds.has(currentObjectID as any)
      ? (items.get(currentObjectID as any) as any)
      : null
  const canShowReplaceButton =
    !!selectedReplaceItem &&
    (selectedReplaceItem.type === 'items' || selectedReplaceItem.type === 'linkedItem')

  const getAutoGroupChildIds = (baseId: string, baseItem?: any) => {
    const state = useAppStore.getState()
    const fromSet = Array.from((baseItem?.childs as Set<string>) || [])
    const fromParentLink = Array.from(state.items.values())
      .filter(
        (it: any) => it?.type === 'linkedSon' && String(it?.firstParentID || '') === String(baseId),
      )
      .map((it: any) => String(it.id))
    return [...new Set([...fromSet, ...fromParentLink])]
  }

  const hasAutoGroupChildren = (baseId: string, baseItem?: any) => {
    const state = useAppStore.getState()
    const childIds = getAutoGroupChildIds(baseId, baseItem)
    const children = childIds
      .map((childId) => state.items.get(childId as any))
      .filter(Boolean) as any[]
    const hasSeat = children.some((child) => {
      if (child?.type !== 'linkedSon') return false
      const ref = String(child?.catalogID || child?.referencia || '').toUpperCase()
      return ref.includes('ASIENTO')
    })
    const hasCushion = children.some((child) => {
      if (child?.type !== 'linkedSon') return false
      const ref = String(child?.catalogID || child?.referencia || '').toUpperCase()
      return ref.includes('COJIN')
    })
    return hasSeat && hasCushion
  }

  const isArmLinkedItem = (sceneItem: any) => {
    if (!sceneItem || sceneItem.type !== 'linkedItem') return false
    return /^BR\d+(-\d+)?$/i.test(String(sceneItem.catalogID || ''))
  }

  type ArmMigrationSnapshot = {
    armId: string
    catalogID: string
    sideSign: 1 | -1
    depth: number
  }

  const resolveArmCatalogByParentDepth = (baseArmCatalog: string, parentItem: any) => {
    const baseMatch = String(baseArmCatalog || '')
      .trim()
      .toUpperCase()
      .match(/^BR\d+/)
    if (!baseMatch) return baseArmCatalog

    const baseCode = baseMatch[0]
    const rawDepthCandidate =
      parentItem?.fondo ??
      parentItem?.depthValue ??
      parentItem?.long ??
      parentItem?.thickness ??
      parentItem?.profundidad ??
      null
    const numericDepth = Number(rawDepthCandidate)
    const catalogText = String(parentItem?.catalogID || '')
    const catalogDepthMatch = catalogText.match(/-(\d{2,3})$/)
    const catalogDepthCm = catalogDepthMatch ? Number(catalogDepthMatch[1]) : 0
    const catalogDepthMm = catalogDepthCm ? catalogDepthCm * 10 : 0
    const depthMmFromNumeric =
      Number.isFinite(numericDepth) && numericDepth > 0
        ? numericDepth <= 300
          ? numericDepth * 10
          : numericDepth
        : 0
    const depthMm = depthMmFromNumeric > 0 ? depthMmFromNumeric : catalogDepthMm
    if (!Number.isFinite(depthMm) || depthMm <= 0) return baseCode
    if (depthMm > 1000) return `${baseCode}-105`
    if (catalogDepthCm === 100 || catalogText.includes('-100')) return `${baseCode}-100`
    return baseCode
  }

  const collectAttachedArmsForParent = (parentId: string, parentMatrix: Matrix4) => {
    const parentPos = new Vector3()
    const parentQuat = new Quaternion()
    const parentXAxis = new Vector3(1, 0, 0)
    parentMatrix.decompose(parentPos, parentQuat, new Vector3())
    parentXAxis.applyQuaternion(parentQuat)

    const snapshots: ArmMigrationSnapshot[] = []
    useAppStore.getState().items.forEach((sceneItem: any, sceneId: string) => {
      if (!isArmLinkedItem(sceneItem)) return
      if (String(sceneItem.firstParentID || '') !== String(parentId)) return

      let sideSign: 1 | -1 = 1
      if (sceneItem.matrix) {
        const armPos = new Vector3()
        sceneItem.matrix.decompose(armPos, new Quaternion(), new Vector3())
        sideSign = armPos.clone().sub(parentPos).dot(parentXAxis) >= 0 ? 1 : -1
      }

      snapshots.push({
        armId: sceneId,
        catalogID: String(sceneItem.catalogID || ''),
        sideSign,
        depth: Number(sceneItem.depth || 1),
      })
    })
    return snapshots
  }

  const purgeSceneItems = (ids: string[]) => {
    if (!ids.length) return
    useAppStore.setState((store: any) => {
      ids.forEach((rid) => {
        store.items.delete(rid)
        store.layers[store.currentLayer].items.delete(rid)
        store.layers[store.currentLayer].selected.delete(rid)
        store.sceneBoundingBoxes.delete(rid)
        store.collisionableItems.delete(rid)
      })
    })
  }

  const migrateAttachedArmsToParent = async (
    armSnapshots: ArmMigrationSnapshot[],
    newParentId: string,
    oldParentId: string,
  ) => {
    if (!armSnapshots.length) return

    const storeApi = useAppStore.getState()
    const liveParent = storeApi.items.get(newParentId as any) as any
    if (!liveParent?.matrix) return

    const { collideItems, drawItem } = storeApi
    const drawOpts = {
      preserveSelection: true,
      suppressCameraFit: true,
      suppressExteriorArmRealign: true,
    } as const

    for (const snapshot of armSnapshots) {
      let liveArm = storeApi.items.get(snapshot.armId as any) as any
      if (!liveArm?.matrix) {
        liveArm = Array.from(storeApi.items.values()).find((sceneItem: any) => {
          if (!isArmLinkedItem(sceneItem)) return false
          if (String(sceneItem.firstParentID || '') !== String(oldParentId)) return false
          const catalog = String(sceneItem.catalogID || '').toUpperCase()
          const snapCatalog = snapshot.catalogID.toUpperCase()
          const snapBase = snapCatalog.match(/^BR\d+/)?.[0] || snapCatalog
          const itemBase = catalog.match(/^BR\d+/)?.[0] || catalog
          return itemBase === snapBase
        }) as any
      }
      if (!liveArm?.matrix) continue

      const oldCatalogUpper = String(liveArm.catalogID || snapshot.catalogID).toUpperCase()
      const armBaseMatch = oldCatalogUpper.match(/^BR(\d+)/i)
      const armBaseCode = armBaseMatch ? `BR${armBaseMatch[1]}` : oldCatalogUpper
      const desiredCatalog = resolveArmCatalogByParentDepth(armBaseCode, liveParent)

      let effectiveArmId = String(liveArm.id)
      if (desiredCatalog.toUpperCase() !== oldCatalogUpper) {
        const newArmId = await drawItem(
          getId('linkedItem'),
          desiredCatalog,
          '',
          true,
          liveArm.matrix.clone(),
          'linkedItem',
          undefined,
          false,
          drawOpts,
        )
        if (!newArmId) continue
        purgeSceneItems([effectiveArmId])
        effectiveArmId = String(newArmId)
        liveArm = useAppStore.getState().items.get(newArmId as any) as any
        if (!liveArm?.matrix) continue
      }

      // Mano D/I del nuevo padre: el brazo migrado va al lado contrario a la
      // letra aunque en la unidad anterior estuviera en el otro lado.
      const handedSide = allowedArmSideForUnit(liveParent?.catalogID)
      const migrationSideSign = handedSide ?? snapshot.sideSign
      const approachMatrix = buildApproachMatrixForArm(liveParent, liveArm, migrationSideSign, 0)
      if (!approachMatrix) continue

      const approachPos = decomposeMatrixPosition(approachMatrix)
      editItem(effectiveArmId, {
        matrix: approachMatrix,
        x: approachPos.x,
        y: approachPos.y,
        z: approachPos.z,
        firstParentID: newParentId,
        depth: snapshot.depth,
      } as any)
      collideItems(effectiveArmId as any, newParentId as any)

      const settledArm = useAppStore.getState().items.get(effectiveArmId as any) as any
      const settledParent = useAppStore.getState().items.get(newParentId as any) as any
      const fineMatrix =
        settledArm && settledParent
          ? buildBr12FineTunedMatrix(settledArm, settledParent, migrationSideSign)
          : null
      if (fineMatrix) {
        const finePos = decomposeMatrixPosition(fineMatrix)
        editItem(effectiveArmId, {
          matrix: fineMatrix,
          x: finePos.x,
          y: finePos.y,
          z: finePos.z,
          firstParentID: newParentId,
          depth: snapshot.depth,
        } as any)
      }
    }
  }

  const replaceSelectedAutoGroup = async (
    selectedBaseId: string,
    newBaseCatalogID: string,
    icono: string,
  ) => {
    const state = useAppStore.getState()
    const selectedBase: any = state.items.get(selectedBaseId)
    if (!selectedBase?.matrix) return false
    const armsToMigrate = collectAttachedArmsForParent(selectedBaseId, selectedBase.matrix)
    const groupChildIds = getAutoGroupChildIds(selectedBaseId, selectedBase)
    const oldSeat: any = groupChildIds
      .map((cid) => state.items.get(cid))
      .find(
        (ch: any) =>
          ch?.type === 'linkedSon' &&
          String(ch?.catalogID || '')
            .toUpperCase()
            .includes('ASIENTO'),
      )
    const oldCushion: any = groupChildIds
      .map((cid) => state.items.get(cid))
      .find(
        (ch: any) =>
          ch?.type === 'linkedSon' &&
          String(ch?.catalogID || '')
            .toUpperCase()
            .includes('COJIN'),
      )

    const oldSeatCatalog = String(oldSeat?.catalogID || oldSeat?.referencia || '').toLowerCase()
    const seatStitchType = oldSeatCatalog.includes('asientosin')
      ? 'sin'
      : oldSeatCatalog.includes('asiento')
        ? 'con'
        : String((selectedBase as any)?.seatStitchType || getSeatStitchForNewUnits()) === 'sin'
          ? 'sin'
          : 'con'
    const seatHeightCm = resolveUnitSeatHeightCm(selectedBase)
    const resolvedNewBaseCatalog = resolveDrawCatalogWithSeatHeight(
      childBaseCatalogFromUnit(newBaseCatalogID),
      seatHeightCm,
    )
    const cushionCatalog = getCushionCatalogForTargetBase(resolvedNewBaseCatalog, oldCushion?.catalogID)
    const seatCatalog = getSeatCatalogForBase(resolvedNewBaseCatalog, seatStitchType as 'con' | 'sin')
    const packGroupId =
      (selectedBase as any)?.packGroupId ||
      `pack_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    const idsToDelete = [selectedBaseId, ...groupChildIds]

    const inheritBaseTextures = cloneLinkedItemTexturesForInheritance(selectedBase)
    const inheritSeatTextures = cloneLinkedItemTexturesForInheritance(oldSeat)
    const inheritCushionTextures = cloneLinkedItemTexturesForInheritance(oldCushion)

    // Eliminar unidad vieja + hijos (no brazos) antes de dibujar la nueva.
    purgeSceneItems(idsToDelete)

    const drawPackOpts = {
      preserveSelection: true,
      suppressCameraFit: true,
      preserveWorldTransform: true,
      suppressAutoSeatAndCushion: true,
      suppressExteriorArmRealign: true,
    } as const

    const { baseId, seatId, cushionsId, baseItemAtDraw, seatY, cushionsY, basePos } =
      await runWithHistoryBatchAsync(async () => {
        const drawnBaseId = await drawItem(
          getId('items'),
          resolvedNewBaseCatalog,
          icono || '',
          false,
          selectedBase.matrix.clone(),
          'linkedItem',
          undefined,
          false,
          {
            ...drawPackOpts,
            inheritTexturesFrom: inheritBaseTextures,
          },
        )
        if (!drawnBaseId) {
          return {
            baseId: null as string | null,
            seatId: null as string | null,
            cushionsId: null as string | null,
            baseItemAtDraw: null as any,
            seatY: 0,
            cushionsY: 0,
            basePos: new Vector3(),
          }
        }

        const refreshedState = useAppStore.getState()
        const drawnBaseItem: any = refreshedState.items.get(drawnBaseId as string)
        const drawnBasePos = new Vector3()
        const baseQuat = new Quaternion()
        const baseScale = new Vector3(1, 1, 1)
        drawnBaseItem?.matrix?.decompose(drawnBasePos, baseQuat, baseScale)

        const baseHalfHeight = (drawnBaseItem?.height || 0) / 2
        const seatHalfHeight = 200
        const computedSeatY =
          drawnBasePos.y + baseHalfHeight + seatHalfHeight - SEAT_EMBED_OFFSET_MM - PACK_EXTRA_Y_OFFSET_MM
        const seatMatrix = new Matrix4().compose(
          new Vector3(drawnBasePos.x, computedSeatY, drawnBasePos.z),
          baseQuat,
          new Vector3(1, 1, 1),
        )
        const drawnSeatId = await drawItem(
          getId('linkedSon'),
          seatCatalog,
          icono || '',
          false,
          seatMatrix,
          'linkedSon',
          undefined,
          false,
          {
            ...drawPackOpts,
            inheritTexturesFrom: inheritSeatTextures,
          },
        )

        const cushionsHalfHeight = 100
        const computedCushionsY =
          computedSeatY +
          seatHalfHeight +
          cushionsHalfHeight -
          CUSHIONS_EMBED_OFFSET_MM -
          PACK_EXTRA_Y_OFFSET_MM
        const cushionsMatrix = new Matrix4().compose(
          new Vector3(drawnBasePos.x, computedCushionsY, drawnBasePos.z),
          baseQuat,
          new Vector3(1, 1, 1),
        )
        const drawnCushionsId = await drawItem(
          getId('linkedSon'),
          String(cushionCatalog),
          icono || '',
          false,
          cushionsMatrix,
          'linkedSon',
          undefined,
          false,
          {
            ...drawPackOpts,
            inheritTexturesFrom: inheritCushionTextures,
          },
        )

        return {
          baseId: drawnBaseId as string,
          seatId: drawnSeatId as string | null,
          cushionsId: drawnCushionsId as string | null,
          baseItemAtDraw: drawnBaseItem,
          seatY: computedSeatY,
          cushionsY: computedCushionsY,
          basePos: drawnBasePos,
        }
      })

    if (!baseId) return false

    const finalState = useAppStore.getState()
    const seatItem = seatId ? (finalState.items.get(seatId as string) as any) : null
    const cushionsItem = cushionsId ? (finalState.items.get(cushionsId as string) as any) : null
    const childPlacement = placementForSeatHeightChange(seatHeightCm)
    const seatOffset = new Vector3(childPlacement.x, childPlacement.y, childPlacement.z)
    const cushionsOffset = new Vector3(childPlacement.x, childPlacement.y, childPlacement.z)

    editItem(
      baseId as string,
      {
        packGroupId,
        packPrimaryId: baseId,
        packSnapWidth: baseItemAtDraw?.width || 0,
        packSnapDepth: baseItemAtDraw?.thickness || 0,
        seatHeightCm,
        seatStitchType,
        childs: new Set([seatId, cushionsId].filter(Boolean) as string[]),
      } as any,
    )
    if (seatId) {
      editItem(
        seatId as string,
        {
          firstParentID: baseId,
          depth: 1,
          x: seatOffset.x,
          y: seatOffset.y,
          z: seatOffset.z,
          isBlocked: true,
          visible: false,
        } as any,
      )
      setCollision(false as any, seatId as any)
    }
    if (cushionsId) {
      editItem(
        cushionsId as string,
        {
          firstParentID: baseId,
          depth: 1,
          x: cushionsOffset.x,
          y: cushionsOffset.y,
          z: cushionsOffset.z,
          isBlocked: true,
          visible: false,
        } as any,
      )
      setCollision(false as any, cushionsId as any)
      setTimeout(() => {
        cloneMaterialBetweenLinkedSons(oldCushion, cushionsId as string, baseId as string)
        forceSceneRefresh()
      }, 0)
    }

    // Activar nuevo pack y eliminar el anterior en un único paso
    if (baseId) editItem(baseId as string, { visible: true } as any)
    if (seatId) editItem(seatId as string, { visible: true } as any)
    if (cushionsId) editItem(cushionsId as string, { visible: true } as any)
    const newBaseItem = useAppStore.getState().items.get(baseId as string) as any
    const replacingWithSingleArmSeries =
      isSingleArmSeries(resolvedNewBaseCatalog) || isSingleArmSeries(newBaseItem?.catalogID)

    if (replacingWithSingleArmSeries && armsToMigrate.length > 0) {
      const placementEval = evaluateSingleArmSeriesPlacement(
        useAppStore.getState().items,
        newBaseItem,
        { getArmSideSign },
      )
      const preferredSide =
        placementEval.allowed &&
        placementEval.appliesSingleArmSeriesRules &&
        String(placementEval.placement.unit.id) === String(baseId)
          ? placementEval.placement.side
          : null
      const snapshots = armsToMigrate.map((arm) => ({
        oldArmId: arm.armId,
        oldCatalogID: arm.catalogID,
        preservedSideSign: arm.sideSign,
        depth: arm.depth,
      }))
      const { keep, remove } = pickArmSnapshotToKeepForSingleArmSeries(snapshots, preferredSide)
      if (remove.length > 0) {
        showArmRestrictionNotice(ARM_NOTICE_ONLY_ONE_SIDE())
        purgeSceneItems(remove.map((arm) => arm.oldArmId))
      }
      if (keep) {
        await migrateAttachedArmsToParent(
          armsToMigrate.filter((arm) => arm.armId === keep.oldArmId),
          baseId as string,
          selectedBaseId,
        )
      }
    } else {
      await migrateAttachedArmsToParent(armsToMigrate, baseId as string, selectedBaseId)
    }
    purgeSceneItems(idsToDelete)

    unselect()
    select(baseId as any, false)
    setCurrentObjectID(baseId as string)
    setOBB(baseId as string)
    forceSceneRefresh()
    return true
  }

  const handleMenuItemClick = async (
    clavemenuabrir: string | null,
    texto: string,
    accion?: string | null,
    args?: string[] | null,
    icono?: string,
    claveMenuItem?: string,
  ) => {
    if (isModalOpen) return

    // Si clavemenuabrir es null y accion es "Objeto", dibujar el objeto
    if (!clavemenuabrir && accion?.toLowerCase() === 'objeto' && args && args.length > 0) {
      const rawCatalogID = args.length > 1 ? args[1] : args[0]
      const catalogID = resolveDrawCatalogWithSeatHeight(
        rawCatalogID,
        getSeatHeightCmForNewUnits(),
      )
      const preDrawStore = useAppStore.getState()
      const anchorIdBeforeUnselect = preDrawStore.currentObjectID
      const drawingRinconero = isModularCornerSnapCatalog(catalogID)
      const drawingCornerInsert = isCornerInsertCatalog(catalogID)

      if (RINCONERO_DRAW_DEBUG) {
        console.warn('[RinconeroDraw][Toolbar.handleMenuItemClick]', {
          catalogID,
          drawingRinconero,
          drawingCornerInsert,
          anchorIdBeforeUnselect,
          claveMenuItem,
          note: 'Atemporal dibuja por Toolbar/index, no MenuItem',
        })
      }

      let chainedDrawMatrix: Matrix4 | null = null
      let chainedDrawSnapSide: 'left' | 'right' | undefined
      let drawItemType: 'linkedItem' | 'items' = 'items'
      let suppressExteriorArmRealign = false
      let resolvedAnchorForDraw: ReturnType<typeof resolveAnchorUnitForRinconeroDraw> = null
      let attachEndForRinconero: 'right' | 'left' = 'right'
      let cornerInsertApplied = false
      let abortCornerDraw = false

      if (
        anchorIdBeforeUnselect &&
        (String(anchorIdBeforeUnselect).startsWith('linkedItem_') ||
          String(anchorIdBeforeUnselect).startsWith('items_'))
      ) {
        resolvedAnchorForDraw = resolveAnchorUnitForRinconeroDraw(
          preDrawStore.items as Map<string, any>,
          anchorIdBeforeUnselect as any,
        )
        if (resolvedAnchorForDraw?.unit?.matrix) {
          const resolved = resolvedAnchorForDraw
          const refIsRinconero = isModularCornerSnapCatalog(resolved.unit.catalogID)
          const drawWidth = defaultWidthForDrawCatalog(catalogID, preDrawStore.sceneGLB)
          const drawThickness = defaultThicknessForDrawCatalog(catalogID, preDrawStore.sceneGLB)

          // Corner/oreja sobre composición existente (166+160…): extremo libre de la
          // cadena, nunca sobre la cara ya ocupada del rinconero.
          if (drawingCornerInsert) {
            const cornerPlan = resolveCornerInsertPlan(
              preDrawStore.items as Map<string, any>,
              resolved.unit,
              catalogID,
              drawWidth,
              drawThickness,
              Number((resolved.unit as any).height || 0),
            )
            if (cornerPlan) {
              attachEndForRinconero = cornerPlan.attachEnd
              // Eliminar BR del extremo / junta Corner↔módulo (nunca migrar).
              const armIds = Array.from(
                new Set([
                  ...cornerPlan.armIdsToRemove,
                  ...collectArmsAtCornerInsertEnd(
                    preDrawStore.items as Map<string, any>,
                    cornerPlan.endUnit,
                    cornerPlan.attachEnd,
                    cornerPlan.matrix,
                    drawWidth,
                  ),
                ]),
              )
              removeArmsForCornerInsert(
                armIds,
                (ids) => useAppStore.getState().removeSceneItemsByIds(ids),
                showArmRestrictionNotice,
              )
              chainedDrawMatrix = cornerPlan.matrix
              chainedDrawSnapSide = cornerPlan.attachEnd
              resolvedAnchorForDraw = {
                unitId: cornerPlan.endUnitId,
                unit: cornerPlan.endUnit,
              }
              // Como RELAX: dejar que realignExteriorArms purgue el BR del labio
              // (newUnitHostsArms=false). No suprimir el realineado.
              suppressExteriorArmRealign = false
              cornerInsertApplied = true
              drawSnapLog('toolbar:corner-insert-plan', {
                catalogID,
                endUnitId: String(cornerPlan.endUnitId),
                endCatalog: cornerPlan.endUnit.catalogID,
                attachEnd: cornerPlan.attachEnd,
                reason: cornerPlan.reason,
                armsRemoved: armIds.map(String),
              })
            } else {
              abortCornerDraw = true
              showArmRestrictionNotice(CORNER_INSERT_NO_FREE_END_NOTICE)
              drawSnapLog('toolbar:corner-insert-abort', {
                catalogID,
                anchorId: String(resolved.unitId),
                anchorCatalog: resolved.unit.catalogID,
              })
            }
          }

          if (!cornerInsertApplied && !abortCornerDraw) {
            // No heredar ancho/fondo del ancla (166): un Relax tomaría 920 y solaparía en pata.
            // Corner XL y módulos rectos (160, RELAX…): extremo de fila sin brazo.
            const chainedDrawPlan =
              (!refIsRinconero && !drawingRinconero) ||
              (drawingRinconero && isCornerXLBrazoCatalog(catalogID) && !refIsRinconero)
                ? resolveChainedDrawPlan(
                    preDrawStore.items as Map<string, any>,
                    resolved.unit,
                    catalogID,
                    drawWidth,
                    resolved.unit as any,
                  )
                : null
            if (drawingRinconero) {
              attachEndForRinconero = resolveAttachEndForCornerDraw(
                catalogID,
                chainedDrawPlan?.offset ?? (isCornerXLBrazoCatalog(catalogID) ? drawWidth : 0),
              )
              removeArmsForRinconeroDraw(
                preDrawStore.items as Map<string, any>,
                resolved.unitId,
                resolved.unit,
                attachEndForRinconero,
                (ids) => useAppStore.getState().removeSceneItemsByIds(ids),
                showArmRestrictionNotice,
                'Toolbar.handleMenuItemClick.preDraw',
              )
              // Como RELAX: realign purga el BR; no suprimir.
              suppressExteriorArmRealign = false
            }
            if (refIsRinconero && !drawingRinconero) {
              chainedDrawSnapSide = resolveDrawSnapSideOnRinconero(
                resolved.unit as any,
                preDrawStore.items as Map<string, any>,
                String(resolved.unitId),
                [],
                drawWidth,
              )
              chainedDrawMatrix = getRelativeMatrix(
                resolved.unit.matrix as Matrix4,
                0,
                resolved.unit as any,
                undefined,
                undefined,
                catalogID,
                chainedDrawSnapSide,
                drawWidth,
                drawThickness,
              )
            } else if (refIsRinconero || (drawingRinconero && !isCornerXLBrazoCatalog(catalogID))) {
              chainedDrawMatrix = getRelativeMatrix(
                resolved.unit.matrix as Matrix4,
                0,
                resolved.unit as any,
                undefined,
                undefined,
                catalogID,
              )
            } else {
              // Fallback fila: derecha = +ancho ancla (no drawWidth); evita Relax↔160 solapados.
              const anchorWidth = Number(resolved.unit.width || 0) || drawWidth
              chainedDrawMatrix =
                chainedDrawPlan?.matrix ??
                getRelativeMatrix(
                  resolved.unit.matrix as Matrix4,
                  anchorWidth,
                  resolved.unit as any,
                  undefined,
                  undefined,
                  catalogID,
                )
              if (chainedDrawPlan?.offset != null && chainedDrawPlan.offset !== 0) {
                chainedDrawSnapSide = chainedDrawPlan.offset < 0 ? 'left' : 'right'
              } else {
                chainedDrawSnapSide = 'right'
              }
            }
          }
          drawSnapLog('toolbar:matriz-encadenada', {
            catalogID,
            anchorId: String(resolvedAnchorForDraw?.unitId ?? resolved.unitId),
            anchorCatalog: resolvedAnchorForDraw?.unit?.catalogID ?? resolved.unit.catalogID,
            refIsRinconero,
            drawingRinconero,
            cornerInsertApplied,
            drawWidth,
            snapSide: chainedDrawSnapSide ?? null,
            face:
              chainedDrawSnapSide && refIsRinconero && !drawingRinconero && !cornerInsertApplied
                ? rinconeroStraightModuleUsesLegSnap(resolved.unit as any, chainedDrawSnapSide)
                  ? 'leg'
                  : 'lateral'
                : null,
          })
          drawItemType = 'linkedItem'
        } else if (RINCONERO_DRAW_DEBUG) {
          console.warn('[RinconeroDraw][Toolbar] sin ancla resuelta', { anchorIdBeforeUnselect })
        }
      } else if (RINCONERO_DRAW_DEBUG && drawingRinconero) {
        console.warn('[RinconeroDraw][Toolbar] sin selección previa — dibujo suelto en origen')
      }

      if (abortCornerDraw) return

      unselect()
      setMode(
        is2DMode(mode)
          ? 'MODE_FORCE'
          : mode === 'MODE_ALZADOS'
            ? 'MODE_FORCE_ALZADOS'
            : 'MODE_FORCE_3D',
      )

      try {
        const autoGroupCatalogs = getAutoGroupCatalogs(claveMenuItem, args)
        if (autoGroupCatalogs && autoGroupCatalogs.length > 0) {
          await runWithHistoryBatchAsync(async () => {
            const createdIds = new Set<any>()
            const packGroupId = `pack_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
            const [baseCatalogID, seatCatalogID, cushionsCatalogID] = autoGroupCatalogs
            const seatHeightCm = getSeatHeightCmForNewUnits()
            const seatStitchType = getSeatStitchForNewUnits()

            // IMPORTANTE: usar la matriz encadenada (incluye colocación en pata de
            // rinconero 166-). Con null, el fallback de drawItem posiciona el pack
            // solo por ancho en X, ignorando el rinconero.
            const baseId = await drawItem(
              getId('items'),
              baseCatalogID,
              icono || '',
              true,
              chainedDrawMatrix,
              'items',
              undefined,
              false,
              {
                useChainedDrawMatrix: Boolean(chainedDrawMatrix),
                chainedDrawSnapSide,
                preDrawSelectedAnchorId:
                  (resolvedAnchorForDraw?.unitId as AnyID | undefined) ??
                  (anchorIdBeforeUnselect as AnyID | undefined),
                suppressExteriorArmRealign,
              },
            )
            if (baseId) createdIds.add(baseId)

            let seatId = ''
            let cushionsId = ''
            const basePos = new Vector3()
            const baseQuat = new Quaternion()
            const baseScale = new Vector3(1, 1, 1)
            const baseItemAtDraw = useAppStore.getState().items.get(baseId as string) as any
            if (baseItemAtDraw?.matrix) {
              baseItemAtDraw.matrix.decompose(basePos, baseQuat, baseScale)
            }

            // Calcular posiciones de apilamiento antes de dibujar

            // Calcular posición del asiento encima de la base
            const baseHalfHeight = (baseItemAtDraw?.height || 0) / 2
            const seatHalfHeight = 200 // Aproximado, se ajustará después
            const seatY =
              basePos.y +
              baseHalfHeight +
              seatHalfHeight -
              SEAT_EMBED_OFFSET_MM -
              PACK_EXTRA_Y_OFFSET_MM
            const seatMatrix = new Matrix4().compose(
              new Vector3(basePos.x, seatY, basePos.z),
              baseQuat,
              new Vector3(1, 1, 1),
            )

            // Dibujar asiento como linkedSon (hijo real del base)
            seatId = await drawItem(
              getId('linkedSon'),
              seatCatalogID,
              icono || '',
              true,
              seatMatrix,
              'linkedSon',
            )
            if (seatId) createdIds.add(seatId)

            // Calcular posición de los cojines encima del asiento
            const cushionsHalfHeight = 100 // Aproximado, se ajustará después
            const cushionsY =
              seatY +
              seatHalfHeight +
              cushionsHalfHeight -
              CUSHIONS_EMBED_OFFSET_MM -
              PACK_EXTRA_Y_OFFSET_MM
            const cushionsMatrix = new Matrix4().compose(
              new Vector3(basePos.x, cushionsY, basePos.z),
              baseQuat,
              new Vector3(1, 1, 1),
            )

            // Dibujar cojines como linkedSon (hijo real del base)
            cushionsId = await drawItem(
              getId('linkedSon'),
              cushionsCatalogID,
              icono || '',
              true,
              cushionsMatrix,
              'linkedSon',
            )
            if (cushionsId) createdIds.add(cushionsId)

            if (createdIds.size >= 2) {
              const childPlacement = placementForSeatHeightChange(seatHeightCm)
              const seatOffset = new Vector3(childPlacement.x, childPlacement.y, childPlacement.z)
              const cushionsOffset = new Vector3(childPlacement.x, childPlacement.y, childPlacement.z)

              editItem(
                baseId as string,
                {
                  packGroupId,
                  packPrimaryId: baseId,
                  packSnapWidth: baseItemAtDraw?.width || 0,
                  packSnapDepth: baseItemAtDraw?.thickness || 0,
                  seatHeightCm,
                  seatStitchType,
                  childs: new Set([seatId, cushionsId].filter(Boolean) as string[]),
                } as any,
              )
              if (seatId) {
                editItem(
                  seatId as string,
                  {
                    firstParentID: baseId,
                    depth: 1,
                    x: seatOffset.x,
                    y: seatOffset.y,
                    z: seatOffset.z,
                    isBlocked: true,
                  } as any,
                )
                setCollision(false as any, seatId as any)
              }
              if (cushionsId) {
                editItem(
                  cushionsId as string,
                  {
                    firstParentID: baseId,
                    depth: 1,
                    x: cushionsOffset.x,
                    y: cushionsOffset.y,
                    z: cushionsOffset.z,
                    isBlocked: true,
                  } as any,
                )
                setCollision(false as any, cushionsId as any)
              }

              if (AUTO_GROUP_DEBUG) {
                console.log('[linked][autogroup][linkedson-group]', {
                  claveMenuItem,
                  ids: { baseId, seatId, cushionsId },
                  message: 'Seat/cushions created as linkedSon childs of base',
                })
              }

              unselect()
              select(baseId as any, false)
              setCurrentObjectID(baseId as string)
              setOBB(baseId as string)
              // Refuerzo para mantener la selección en el padre.
              setTimeout(() => {
                unselect()
                select(baseId as any, false)
                setCurrentObjectID(baseId as string)
              }, 0)
            } else if (createdIds.size === 1) {
              const [singleId] = Array.from(createdIds)
              setCurrentObjectID(singleId as string)
              setOBB(singleId as string)
            }
          })
        } else {
          const id = getId(drawItemType === 'linkedItem' ? 'linkedItem' : 'items')
          const reid = await drawItem(
            id,
            catalogID,
            icono || '',
            true,
            chainedDrawMatrix,
            drawItemType,
            undefined,
            false,
            {
              suppressExteriorArmRealign,
              useChainedDrawMatrix: Boolean(chainedDrawMatrix),
              chainedDrawSnapSide,
              preDrawSelectedAnchorId:
                (resolvedAnchorForDraw?.unitId as AnyID | undefined) ??
                (anchorIdBeforeUnselect as AnyID | undefined),
            },
          )
          drawSnapLog('toolbar:draw-completado', {
            catalogID,
            drawItemType,
            anchorId: String(resolvedAnchorForDraw?.unitId ?? anchorIdBeforeUnselect ?? ''),
            anchorCatalog: resolvedAnchorForDraw?.unit?.catalogID,
            refIsRinconero: isModularCornerSnapCatalog(resolvedAnchorForDraw?.unit?.catalogID),
            usedChainedMatrix: Boolean(chainedDrawMatrix),
            snapSide: chainedDrawSnapSide ?? null,
            newItemId: String(reid),
          })
          if ((drawingRinconero || cornerInsertApplied) && resolvedAnchorForDraw?.unit) {
            const postStore = useAppStore.getState()
            const endUnit =
              postStore.items.get(String(resolvedAnchorForDraw.unitId)) ||
              postStore.items.get(resolvedAnchorForDraw.unitId as any) ||
              resolvedAnchorForDraw.unit
            if (cornerInsertApplied) {
              const drawnCorner = postStore.items.get(reid) as any
              const cornerMat =
                (drawnCorner?.matrix as Matrix4 | undefined) ?? chainedDrawMatrix
              const cornerW =
                Number(drawnCorner?.width || 0) ||
                defaultWidthForDrawCatalog(catalogID, postStore.sceneGLB) ||
                0
              removeArmsForCornerInsert(
                collectArmsAtCornerInsertEnd(
                  postStore.items as Map<string, any>,
                  endUnit,
                  attachEndForRinconero,
                  cornerMat,
                  cornerW,
                ),
                (ids) => useAppStore.getState().removeSceneItemsByIds(ids),
                showArmRestrictionNotice,
              )
              postStore.setOBBScene?.()
            } else {
              removeArmsForRinconeroDraw(
                postStore.items as Map<string, any>,
                resolvedAnchorForDraw.unitId,
                endUnit,
                attachEndForRinconero,
                (ids) => postStore.removeSceneItemsByIds(ids),
                showArmRestrictionNotice,
                'Toolbar.handleMenuItemClick.postDraw',
              )
            }
          }
          setCurrentObjectID(reid)
          setOBB(reid)
        }
      } finally {
        setTimeout(() => {
          setMode(
            is2DMode(mode)
              ? 'MODE_IDLE'
              : mode === 'MODE_FORCE_ALZADOS'
                ? 'MODE_ALZADOS'
                : 'MODE_3D_VIEW',
          )
        }, 100)
      }
      return
    }

    // Si tiene clavemenuabrir, navegar al submenú en el mismo panel
    if (clavemenuabrir) {
      setNavStack((prev) => [...prev, { clavemenuabrir, texto }])
      setSearchText('')
      return
    }
  }

  return (
    <aside
      ref={scrollContainerRef}
      className={
        presentation === 'embedded'
          ? 'scrollable relative min-h-0 w-full overflow-x-hidden bg-white px-3 pb-6 pt-0'
          : 'scrollable fixed bottom-0 left-0 top-20 z-10 w-80 overflow-y-auto overflow-x-hidden  bg-white px-4 pb-24 pt-0'
      }
    >
      <div className='sticky top-0 z-20 mb-3 bg-white pb-2'>
        {presentation !== 'embedded' && isPrincipalView ? (
          <div className='mb-2 rounded-2xl border border-[#D7C9A6]/55 bg-white/95 px-2.5 py-1.5 shadow-[0_6px_16px_rgba(0,0,0,0.06)] backdrop-blur-[2px]'>
            <div className='flex items-center gap-2.5'>
              <span className='flex h-10 w-10 items-center justify-center rounded-full border border-[#C8A85A] bg-gradient-to-b from-[#E1C57A] to-[#C8A85A] text-xl font-extrabold text-white shadow-[0_3px_8px_rgba(200,168,90,0.4)]'>
                1
              </span>
              <div className='leading-tight'>
                <p className='text-base font-semibold tracking-[0.02em] text-C/Color3'>
                  {ui('chooseModules')}
                </p>
              </div>
            </div>
          </div>
        ) : null}
        {!isPrincipalView ? (
          <div className='mb-2 flex items-center gap-2'>
            <button
              type='button'
              onClick={handleBackNavigation}
              className='group flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#C8A85A]/70 bg-gradient-to-b from-[#FAF4E8] to-[#F0E4C8] text-[#8B6F2E] shadow-[0_2px_8px_rgba(200,168,90,0.25)] transition-all hover:scale-105 hover:border-[#C8A85A] hover:shadow-[0_4px_12px_rgba(200,168,90,0.35)] active:scale-95'
              aria-label={ui('backToMainMenu')}
            >
              <svg
                className='h-5 w-5 transition-transform group-hover:-translate-x-0.5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2.5}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
            </button>
            <div className='min-w-0 flex-1 leading-tight'>
              <p className='truncate text-base font-semibold tracking-[0.02em] text-C/Color3'>
                {localizeCatalogLabel(currentCategoryTitle)}
              </p>
              <button
                type='button'
                onClick={handleBackToPrincipal}
                className='mt-0.5 text-xs font-medium text-[#8B6F2E] transition-colors hover:text-[#6B5420] hover:underline'
              >
                {ui('backToPrincipal')}
              </button>
            </div>
          </div>
        ) : null}
        <div className='relative'>
          <input
            type='text'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={isPrincipalView ? ui('searchByWidth') : ui('search')}
            className='w-full rounded-xl border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-C/Color3 outline-none transition-all focus:border-colorAtemporal focus:bg-white focus:ring-2 focus:ring-colorAtemporal/20'
          />
          <svg
            className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z'
            />
          </svg>
          {searchText && (
            <button
              type='button'
              onClick={() => setSearchText('')}
              className='absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700'
              aria-label='Limpiar búsqueda'
            >
              x
            </button>
          )}
        </div>
      </div>

      {/* Grid: Desktop 2 cols histórico; embedded densifica por mobile / tabletV / tabletH */}
      <div
        className={
          presentation === 'embedded'
            ? getEmbeddedModulesGridClass(layout)
            : 'grid grid-cols-2 gap-5'
        }
      >
        {visibleSubmenus.map(
          (
            {
              id,
              claveMenuItem,
              clavemenuabrir,
              orden,
              texto,
              referencia,
              codigoUnidad,
              descripcion,
              fondo,
              ancho,
              alto,
              icono,
              altoicono,
              anchoicono,
              accion,
              args,
            },
            index,
          ) => {
            // Usar logoAtemporal solo para ATEMPORAL
            const isAtemporal = claveMenuItem === 'ATEMPORAL'
            const finalIcono = isAtemporal ? logoAtemporal : icono
            // Imágenes más grandes para mejor visibilidad
            const finalAltoicono = isAtemporal ? (altoicono ?? 120) : (altoicono ?? 110)
            const finalAnchoicono = isAtemporal ? (anchoicono ?? 180) : (anchoicono ?? 140)
            const embeddedScale =
              presentation === 'embedded' ? getEmbeddedModuleScale(layout) : 1
            const displayAlto = Math.round(finalAltoicono * embeddedScale)
            const displayAncho = Math.round(finalAnchoicono * embeddedScale)
            const embeddedMaxH =
              presentation === 'embedded' ? getEmbeddedModuleMaxHeight(layout) : 120
            const refSource = String(referencia || codigoUnidad || texto || '').trim()
            const refNumber = refSource.split('-')[0]?.trim() || refSource
            const toCm = (value: unknown) => {
              if (typeof value !== 'number' || Number.isNaN(value)) return null
              return Math.round(value / 10)
            }
            const largoCm = toCm(ancho)
            const fondoCm = toCm(fondo)
            const altoCm = toCm(alto)
            const showSpecs = Boolean(codigoUnidad || referencia)
            const showModuleSpecs =
              (!isPrincipalView || normalizedSearch.length > 0) &&
              (showSpecs || alto != null || ancho != null || fondo != null)
            const moduleTitle = localizeCatalogLabel(
              String(descripcion || codigoUnidad || referencia || texto || '').trim() || texto,
            )
            const displayTexto = localizeCatalogLabel(texto)
            const dimLabels = getCatalogDimensionLabels()

            const handleReplaceItem = async (e: React.MouseEvent) => {
              e.stopPropagation()
              if (!currentObjectID) return
              const usingItem = items.get(currentObjectID)
              if (!usingItem) return
              const rawCatalogID =
                args && args.length > 1 ? args[1] : args && args.length > 0 ? args[0] : ''
              const realCatalogID = args && args.length === 2 ? args[0] : undefined
              if (!rawCatalogID) return
              const { catalogID, seatHeightCm } = resolveReplaceCatalogWithSeatHeight(
                rawCatalogID,
                usingItem,
              )
              await withCameraFitBlocked(async () => {
                const isSelectedPackBase =
                  (usingItem as any)?.type === 'linkedItem' &&
                  hasAutoGroupChildren(usingItem.id as string, usingItem as any)
                if (isSelectedPackBase) {
                  const replaced = await replaceSelectedAutoGroup(
                    usingItem.id as string,
                    rawCatalogID,
                    icono || '',
                  )
                  if (replaced) return
                  return
                }
                const hasSeatChildren = getAutoGroupChildIds(usingItem.id as string, usingItem as any)
                  .map((cid) => items.get(cid as any))
                  .some((child: any) => {
                    const ref = String(child?.catalogID || '').toLowerCase()
                    return ref.includes('asiento') || ref.includes('cojin')
                  })
                const replacedId = await replaceItem(
                  usingItem.id,
                  catalogID,
                  icono || '',
                  'linkedItem',
                  realCatalogID,
                  hasSeatChildren
                    ? { preserveSeatCushionChildren: true, targetSeatHeightCm: seatHeightCm }
                    : undefined,
                )
                if (replacedId) {
                  editItem(replacedId as any, { seatHeightCm } as any)
                  unselect()
                  select(replacedId as any, false)
                  setCurrentObjectID(replacedId as string)
                }
              })
            }

            return (
              <div
                key={`${id}-${claveMenuItem}-${index}`}
                onClick={() =>
                  handleMenuItemClick(clavemenuabrir, texto, accion, args, icono, claveMenuItem)
                }
                className={clsx(
                  'group relative flex flex-col items-center rounded-lg transition-all duration-200',
                  presentation === 'embedded' ? 'px-1 py-2' : 'px-2 py-3',
                  !isModalOpen && 'cursor-pointer hover:bg-gray-50',
                )}
              >
                {/* Miniatura: el sustituir se ancla a la imagen, no a la celda del grid */}
                <div className='relative mx-auto w-fit max-w-full'>
                  {accion?.toLowerCase() === 'objeto' && canShowReplaceButton && (
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <button
                            type='button'
                            onClick={handleReplaceItem}
                            className={clsx(
                              'absolute z-10 flex cursor-pointer items-center justify-center rounded-full bg-white shadow-md transition-all hover:scale-110 hover:bg-gray-100 hover:shadow-lg',
                              // Desktop: posición histórica. Embedded: un poco más arriba/afuera en la esquina de la miniatura.
                              presentation === 'embedded'
                                ? '-right-1.5 -top-2.5 h-8 w-8'
                                : '-right-1 -top-1 h-7 w-7',
                            )}
                            aria-label={'Sustituye el elemento que tengas seleccionado por este'}
                          >
                            <img
                              src={actualizarImg}
                              alt='Reemplazar'
                              className='pointer-events-none h-5 w-5 select-none'
                              draggable='false'
                            />
                          </button>
                        </Tooltip.Trigger>
                        <Tooltip.Content
                          side='left'
                          align='center'
                          className='z-50 rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-black outline outline-[0.5px] outline-C/Color3'
                        >
                          {'Sustituye el elemento que tengas seleccionado por este'}
                          <Tooltip.Arrow className='fill-C/Color1' />
                        </Tooltip.Content>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  )}
                  <div
                    className='flex items-center justify-center'
                    {...(!isModalOpen && finalIcono
                      ? getHoverPreviewHandlers(finalIcono, displayTexto)
                      : {})}
                  >
                    <img
                      className='pointer-events-none object-contain transition-transform duration-300 group-hover:scale-[1.02]'
                      style={{
                        height: displayAlto,
                        width: displayAncho,
                        maxHeight: presentation === 'embedded' ? embeddedMaxH : 120,
                        maxWidth: presentation === 'embedded' ? '100%' : '180px',
                      }}
                      src={finalIcono}
                      alt={displayTexto}
                      loading='lazy'
                    />
                  </div>
                </div>
                {/* Texto justo debajo de la imagen - muy cerca */}
                {showModuleSpecs ? (
                  <div
                    className={clsx(
                      'mt-1 text-C/Color3 transition-colors duration-200',
                      presentation === 'embedded'
                        ? 'mx-auto max-w-full text-center text-xs leading-tight'
                        : 'mx-auto ml-1 text-left text-sm',
                    )}
                    style={{
                      lineHeight: presentation === 'embedded' ? '1.15' : '1.2',
                      width:
                        typeof displayAncho === 'number'
                          ? `${displayAncho}px`
                          : displayAncho,
                    }}
                  >
                    <p className='font-semibold'>{moduleTitle}</p>
                    <p>{formatDimensionLabel(dimLabels.largo, ancho)}</p>
                    <p>{formatDimensionLabel(dimLabels.fondo, fondo)}</p>
                    <p>{formatDimensionLabel(dimLabels.alto, alto)}</p>
                  </div>
                ) : showSpecs ? (
                  <div
                    className={clsx(
                      'mt-1 text-C/Color3 transition-colors duration-200',
                      presentation === 'embedded'
                        ? 'mx-auto max-w-full text-center text-xs leading-tight'
                        : 'mx-auto ml-1 text-left text-sm',
                    )}
                    style={{
                      lineHeight: presentation === 'embedded' ? '1.15' : '1.2',
                      width:
                        typeof displayAncho === 'number'
                          ? `${displayAncho}px`
                          : displayAncho,
                    }}
                  >
                    <p className='font-semibold'>{`${ui('reference')}: ${refNumber}`}</p>
                    <p>{largoCm != null ? `${dimLabels.largo}: ${largoCm}cm` : `${dimLabels.largo}: -`}</p>
                    <p>{fondoCm != null ? `${dimLabels.fondo}: ${fondoCm}cm` : `${dimLabels.fondo}: -`}</p>
                    <p>{altoCm != null ? `${dimLabels.alto}: ${altoCm}cm` : `${dimLabels.alto}: -`}</p>
                  </div>
                ) : (
                  <p
                    className={clsx(
                      'mt-1 w-full text-center font-medium text-C/Color3 transition-colors duration-200 group-hover:text-C/Color3',
                      presentation === 'embedded' ? 'text-xs' : 'text-sm',
                    )}
                    style={{
                      wordBreak: 'break-word',
                      lineHeight: '1.2',
                    }}
                  >
                    {displayTexto}
                  </p>
                )}
              </div>
            )
          },
        )}
      </div>
      {visibleSubmenus.length === 0 && (
        <p className='mt-6 text-center text-sm text-gray-500'>
          {isPrincipalView &&
          normalizedSearch.length > 0 &&
          principalCatalogStatus === 'loading'
            ? ui('searchingModules')
            : ui('noResultsFound')}
        </p>
      )}

      {/* Botón circular flotante para scroll — solo docked (en sheet el FAB fixed se solapa). */}
      {showScrollButton && presentation !== 'embedded' && (
        <div className='pointer-events-none fixed bottom-3 left-[299px] z-30 flex justify-end pr-2'>
          <button
            onClick={handleScrollClick}
            className={clsx(
              'pointer-events-auto -mr-7 h-10 w-10 rounded-full',
              'bg-gray-200/40 backdrop-blur-sm hover:bg-gray-200/60',
              'flex items-center justify-center transition-all duration-300',
              'border border-gray-300/30 hover:scale-105 active:scale-95',
            )}
            aria-label={isAtBottom ? 'Subir' : 'Bajar'}
          >
            <svg
              className={clsx(
                'h-5 w-5 text-gray-600/80 transition-transform duration-300',
                isAtBottom && 'rotate-180',
              )}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 14l-7 7m0 0l-7-7m7 7V3'
              />
            </svg>
          </button>
        </div>
      )}
      <HoverZoomPreviewOverlay preview={hoverPreview} />
    </aside>
  )
}
